import { useJoinWaitlistMutation } from 'store/services/waitlist/waitlistApi';
import { JoinWaitlistTabProps } from './components/LoginForm/forms/JoinWaitlistTab/types';
import { ApiError } from 'store/types';
import { useLoginMutation } from 'store/services/auth/authApi';
import { LoginTabProps } from './components/LoginForm/forms/LoginTab/types';
import { useDispatch } from 'react-redux';
import { loginSuccess } from 'store/services/auth/authSlice';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { meApi } from 'store/services/user/meApi';
import { haveListApi } from 'store/services/collectionList/haveList/haveListApi';
import { wantListApi } from 'store/services/collectionList/wantList/wantListApi';
import { tradeApi } from 'store/services/trade/tradeApi';

export const useLogin = (): {
  loginTabProps: LoginTabProps;
  joinWaitlistTabProps: JoinWaitlistTabProps;
} => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [joinWaitlist, joinWaitlistState] = useJoinWaitlistMutation();
  const [login, loginState] = useLoginMutation();
  const onLoginPress = async (email: string, password: string) => {
    try {
      // Clear all cached data before logging in a new user
      dispatch(meApi.util.resetApiState());
      dispatch(haveListApi.util.resetApiState());
      dispatch(wantListApi.util.resetApiState());
      dispatch(tradeApi.util.resetApiState());
      
      const result = await login({ email, password }).unwrap();
      
      // Pass token, user data (with roles), and CSRF token to the store
      dispatch(loginSuccess({ 
        token: result.token,
        user: result.user,
        csrfToken: result.csrfToken  // New CSRF token from backend
      }));
      
      // Force refetch of user data after login
      dispatch(meApi.util.invalidateTags(['User']));
      
      navigate('/my-cards');
    } catch (e: any) {
      // Log error without sensitive details in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Login failed - Status:', e.status);
      }
      
      // Show error toast directly here as well
      let errorMessage = 'Login failed. Please try again.';
      if (e.status === 401) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (e.status === 404) {
        errorMessage = 'No account found with this email address. Please sign up first.';
      } else if (e.data?.message) {
        errorMessage = e.data.message;
      }
      
      toast.error(errorMessage);
      // Error will also be displayed via loginState.error in the form
    }
  };

  return {
    loginTabProps: {
      ...loginState,
      error: loginState.error as ApiError,
      login: onLoginPress,
    },
    joinWaitlistTabProps: {
      ...joinWaitlistState,
      error: joinWaitlistState.error as ApiError,
      joinWaitlist: async (email: string) => {
        try {
          await joinWaitlist({ email }).unwrap();
          toast.success('Successfully joined the waitlist! We\'ll notify you when space is available.');
        } catch (error: any) {
          toast.error(error.data?.message || 'Failed to join waitlist. Please try again.');
        }
      },
    },
  };
};
